import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Upload, CheckCircle, AlertTriangle, Camera, FolderOpen, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';
import ConfirmModal from '../components/ConfirmModal';
import VerificacionCliente from '../components/VerificacionCliente';
import { logErrorToBackend } from '../utils/errorLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Formulario2() {
  const { expediente_id } = useParams(); // Puede venir de URL antigua o null
  const navigate = useNavigate();
  const [expedienteVerificado, setExpedienteVerificado] = useState(null);
  const [formData, setFormData] = useState({
    nombreCompleto: '',
    fechaInscripcion: '',
    montoAcumulado: '',
    planAdquirido: '',
    fechaUltimoPago: '',
    motivoRetiro: ''
  });
  const [certificacionBancaria, setCertificacionBancaria] = useState(null);
  const [recibos, setRecibos] = useState([]);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingRecibos, setUploadingRecibos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Obtener ID del expediente (desde URL o desde verificación)
  const idExpediente = expedienteVerificado?.id || expediente_id;

  // Helper: XMLHttpRequest con timeout y reintento
  const xhrRequest = (method, url, options = {}) => {
    const { body, headers = {}, timeout = 30000, retries = 1 } = options;
    
    const attempt = (retriesLeft) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.timeout = timeout;
      if (options.withCredentials) xhr.withCredentials = true;
      
      Object.entries(headers).forEach(([key, val]) => xhr.setRequestHeader(key, val));
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          reject(new Error('Respuesta inválida del servidor'));
        }
      };
      
      xhr.onerror = function() {
        if (retriesLeft > 0) {
          setTimeout(() => attempt(retriesLeft - 1).then(resolve).catch(reject), 2000);
        } else {
          reject(new Error('Error de conexión. Verifica tu internet e intenta nuevamente.'));
        }
      };
      
      xhr.ontimeout = function() {
        if (retriesLeft > 0) {
          setTimeout(() => attempt(retriesLeft - 1).then(resolve).catch(reject), 2000);
        } else {
          reject(new Error('La conexión tardó demasiado. Verifica tu internet e intenta nuevamente.'));
        }
      };
      
      xhr.send(body || null);
    });
    
    return attempt(retries);
  };

  const uploadToBackend = async (file, nombreNormalizado = null) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    const result = await xhrRequest('POST', `${BACKEND_URL}/api/upload/documento`, {
      body: uploadFormData,
      timeout: 60000,
      retries: 2
    });

    if (!result.ok) {
      throw new Error(parseApiError(result.data?.detail, 'Error al subir archivo'));
    }

    return {
      nombre: nombreNormalizado || file.name,
      url: result.data.url,
      cloudinary_id: result.data.cloudinary_id
    };
  };

  const handleCertificacionUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo - Más flexible para dispositivos móviles
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
    
    // En móviles, image/* puede venir sin MIME específico
    const isMobileImage = file.type.startsWith('image/');
    
    if (!isValidType && !isMobileImage && file.type !== 'application/pdf') {
      toast.error('Solo se permiten imágenes o archivos PDF');
      return;
    }

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`El archivo es demasiado grande (máximo 10MB). Tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    setUploadingCert(true);
    try {
      const uploadedDoc = await uploadToBackend(file, 'Certificación Bancaria');  // Nombre normalizado
      setCertificacionBancaria(uploadedDoc);
      toast.success('Certificación bancaria subida');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al subir certificación. Intenta nuevamente.');
    } finally {
      setUploadingCert(false);
    }
  };

  const handleRecibosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validar cantidad
    if (recibos.length + files.length > 10) {
      toast.error('Máximo 10 recibos permitidos');
      return;
    }

    setUploadingRecibos(true);
    try {
      const uploadedRecibos = [];
      
      for (const [index, file] of files.entries()) {
        // Validar tipo - Más flexible para dispositivos móviles
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif'];
        const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
        
        // En móviles, image/* puede venir sin MIME específico
        const isMobileImage = file.type.startsWith('image/');
        
        if (!isValidType && !isMobileImage && file.type !== 'application/pdf') {
          toast.error(`${file.name}: Solo imágenes o archivos PDF`);
          continue;
        }

        // Validar tamaño
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: Demasiado grande (máx 10MB)`);
          continue;
        }

        // Usar nombre normalizado para recibos
        const nombreRecibo = `Recibo de Pago #${recibos.length + index + 1}`;
        const uploadedDoc = await uploadToBackend(file, nombreRecibo);
        uploadedRecibos.push(uploadedDoc);
      }

      setRecibos([...recibos, ...uploadedRecibos]);
      toast.success(`${uploadedRecibos.length} recibo(s) subido(s)`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir recibos');
    } finally {
      setUploadingRecibos(false);
    }
  };

  const eliminarRecibo = (index) => {
    setRecibos(recibos.filter((_, i) => i !== index));
    toast.success('Recibo eliminado');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validaciones de campos nuevos
    if (!formData.nombreCompleto.trim()) {
      toast.error('El nombre completo es obligatorio');
      return;
    }

    if (!formData.fechaInscripcion) {
      toast.error('La fecha de inscripción es obligatoria');
      return;
    }

    if (!formData.montoAcumulado.trim()) {
      toast.error('El monto acumulado es obligatorio');
      return;
    }

    if (!formData.planAdquirido.trim()) {
      toast.error('El plan adquirido es obligatorio');
      return;
    }

    if (!formData.fechaUltimoPago) {
      toast.error('La fecha de último pago es obligatoria');
      return;
    }

    // Validaciones existentes
    if (formData.motivoRetiro.length < 50) {
      toast.error('El motivo debe tener al menos 50 caracteres');
      return;
    }

    if (formData.motivoRetiro.length > 150) {
      toast.error('El motivo no debe exceder 150 caracteres');
      return;
    }

    if (!certificacionBancaria) {
      toast.error('Debes subir la certificación bancaria');
      return;
    }

    if (recibos.length === 0) {
      toast.error('Debes subir al menos un recibo');
      return;
    }

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const confirmarEnvio = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nombre_completo', formData.nombreCompleto);
      formDataToSend.append('fecha_inscripcion', formData.fechaInscripcion);
      formDataToSend.append('monto_acumulado', formData.montoAcumulado);
      formDataToSend.append('plan_adquirido', formData.planAdquirido);
      formDataToSend.append('fecha_ultimo_pago', formData.fechaUltimoPago);
      formDataToSend.append('motivo_retiro_detallado', formData.motivoRetiro);
      formDataToSend.append('certificacion_bancaria', JSON.stringify(certificacionBancaria));
      formDataToSend.append('recibos', JSON.stringify(recibos));

      const result = await xhrRequest('POST', `${BACKEND_URL}/api/expedientes/${idExpediente}/formulario-2`, {
        body: formDataToSend,
        timeout: 30000,
        retries: 1
      });

      if (result.ok) {
        toast.success('Formulario enviado exitosamente');
        
        const expedienteNumero = expedienteVerificado?.numero_expediente || idExpediente;
        setTimeout(() => {
          navigate('/formulario-2-exitoso', {
            state: { expedienteNumero }
          });
        }, 2000);
      } else {
        const errorMsg = parseApiError(result.data?.detail, 'Error al enviar formulario');
        toast.error(errorMsg + '. Por favor, intente nuevamente o contacte a soporte.');
        logErrorToBackend({ tipo: 'formulario2_error', endpoint: `/api/expedientes/${idExpediente}/formulario-2`, error: errorMsg });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error de conexion. Verifique su internet e intente nuevamente.');
      logErrorToBackend({ tipo: 'formulario2_conexion_error', endpoint: `/api/expedientes/${idExpediente}/formulario-2`, error: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const caracteresRestantes = 150 - formData.motivoRetiro.length;

  const handleVerificado = (expediente) => {
    setExpedienteVerificado(expediente);
  };

  const mostrarVerificacion = !expediente_id && !expedienteVerificado;

  // DOM estable (React 19): ambas vistas permanecen montadas y se alternan con display:none.
  // Evita el crash "NotFoundError: Failed to execute 'insertBefore'" al resolver
  // buscar-expediente y disparar el toast durante el cambio de vista.
  return (
    <div key="formulario2-stable-root" data-testid="formulario2-root">
      <div style={{ display: mostrarVerificacion ? 'block' : 'none' }}>
        <VerificacionCliente
          onVerificado={handleVerificado}
          titulo="Formulario 2"
          descripcion="Completa la información adicional de tu retiro"
        />
      </div>

      <div style={{ display: mostrarVerificacion ? 'none' : 'block' }} className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-20 w-auto mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">Formulario de Retiro - Paso 2</h1>
          <p className="text-gray-400">Completa la información adicional para procesar tu retiro</p>
          {expedienteVerificado && (
            <p className="text-yellow-500 text-sm mt-2">
              Expediente: {expedienteVerificado.numero_expediente}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos Personales */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Información Personal</CardTitle>
              <CardDescription className="text-gray-400">
                Completa tus datos personales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nombres y Apellidos Completos <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.nombreCompleto ?? ''}
                  onChange={(e) => setFormData({ ...formData, nombreCompleto: e.target.value })}
                  placeholder="Juan Pérez García"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="form2-nombre-input"
                  className="bg-gray-700/50 border-gray-600 text-white"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Información del Plan */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Información del Plan</CardTitle>
              <CardDescription className="text-gray-400">
                Datos de tu participación en KOMPA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fecha de Inscripción <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.fechaInscripcion ?? ''}
                  onChange={(e) => setFormData({ ...formData, fechaInscripcion: e.target.value })}
                  data-testid="form2-fecha-inscripcion-input"
                  className="bg-gray-700/50 border-gray-600 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Monto Acumulado <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.montoAcumulado ?? ''}
                  onChange={(e) => setFormData({ ...formData, montoAcumulado: e.target.value })}
                  placeholder="Ej: 5000 USD o 150.000 Bs"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="form2-monto-input"
                  className="bg-gray-700/50 border-gray-600 text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Indica el monto y la moneda</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plan Adquirido <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.planAdquirido ?? ''}
                  onChange={(e) => setFormData({ ...formData, planAdquirido: e.target.value })}
                  placeholder="Ej: Plan Premium, Plan Básico"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="form2-plan-input"
                  className="bg-gray-700/50 border-gray-600 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fecha de Último Pago <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.fechaUltimoPago ?? ''}
                  onChange={(e) => setFormData({ ...formData, fechaUltimoPago: e.target.value })}
                  data-testid="form2-fecha-pago-input"
                  className="bg-gray-700/50 border-gray-600 text-white"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Motivo del Retiro */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Motivo Detallado del Retiro</CardTitle>
              <CardDescription className="text-gray-400">
                Explica brevemente por qué deseas realizar este retiro (50-150 caracteres)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.motivoRetiro ?? ''}
                onChange={(e) => setFormData({ ...formData, motivoRetiro: e.target.value })}
                placeholder="Ejemplo: Necesito los fondos para un proyecto personal urgente..."
                autoCapitalize="sentences"
                autoCorrect="on"
                data-testid="form2-motivo-input"
                className="bg-gray-700/50 border-gray-600 text-white min-h-[100px]"
                minLength={50}
                maxLength={150}
              />
              <p className={`text-xs mt-2 ${
                formData.motivoRetiro.length < 50 
                  ? 'text-red-400' 
                  : caracteresRestantes < 20 
                  ? 'text-yellow-400' 
                  : 'text-gray-500'
              }`}>
                {formData.motivoRetiro.length}/150 caracteres
                {formData.motivoRetiro.length < 50 && ` (mínimo 50)`}
              </p>
            </CardContent>
          </Card>

          {/* Certificación Bancaria */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Certificación Bancaria</CardTitle>
              <CardDescription className="text-gray-400">
                Sube tu certificación bancaria (PDF o imagen)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {certificacionBancaria ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm text-white font-medium">{certificacionBancaria.nombre}</p>
                      <p className="text-xs text-gray-400">Archivo subido exitosamente</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setCertificacionBancaria(null)}
                    variant="outline"
                    size="sm"
                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                  >
                    Eliminar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Inputs ocultos */}
                  <input
                    type="file"
                    id="cert-camera"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCertificacionUpload}
                    disabled={uploadingCert}
                  />
                  <input
                    type="file"
                    id="cert-gallery"
                    className="hidden"
                    accept="image/*,.pdf,.heic,.heif"
                    onChange={handleCertificacionUpload}
                    disabled={uploadingCert}
                  />
                  
                  {/* Botones de selección */}
                  <div className="grid grid-cols-2 gap-3">
                    <label 
                      htmlFor="cert-camera" 
                      className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className={`w-8 h-8 text-yellow-500 animate-spin ${uploadingCert ? '' : 'hidden'}`} />
                        <Camera className={`w-8 h-8 text-yellow-500 ${uploadingCert ? 'hidden' : ''}`} />
                        <p className="text-gray-300 font-medium text-sm">
                          {uploadingCert ? 'Subiendo...' : 'Tomar Foto'}
                        </p>
                        <p className="text-xs text-gray-500">Usar cámara</p>
                      </div>
                    </label>
                    
                    <label 
                      htmlFor="cert-gallery" 
                      className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className={`w-8 h-8 text-yellow-500 animate-spin ${uploadingCert ? '' : 'hidden'}`} />
                        <FolderOpen className={`w-8 h-8 text-blue-500 ${uploadingCert ? 'hidden' : ''}`} />
                        <p className="text-gray-300 font-medium text-sm">
                          {uploadingCert ? 'Subiendo...' : 'Elegir Archivo'}
                        </p>
                        <p className="text-xs text-gray-500">Galería/PDF</p>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recibos */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Recibos</CardTitle>
              <CardDescription className="text-gray-400">
                Sube todos los recibos que tengas (PDF o imágenes, máximo 10 archivos)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Lista de recibos */}
              {recibos.length > 0 && (
                <div className="space-y-2">
                  {recibos.map((recibo, index) => (
                    <div
                      key={index}
                      className="bg-gray-700/30 border border-gray-600 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-blue-500" />
                        <p className="text-sm text-white">{recibo.nombre}</p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => eliminarRecibo(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-500 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload de recibos */}
              {recibos.length < 10 && (
                <div className="space-y-3">
                  {/* Inputs ocultos */}
                  <input
                    type="file"
                    id="recibos-camera"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handleRecibosUpload}
                    disabled={uploadingRecibos}
                  />
                  <input
                    type="file"
                    id="recibos-gallery"
                    className="hidden"
                    accept="image/*,.pdf,.heic,.heif"
                    multiple
                    onChange={handleRecibosUpload}
                    disabled={uploadingRecibos}
                  />
                  
                  {/* Botones de selección */}
                  <div className="grid grid-cols-2 gap-3">
                    <label 
                      htmlFor="recibos-camera" 
                      className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className={`w-8 h-8 text-yellow-500 animate-spin ${uploadingRecibos ? '' : 'hidden'}`} />
                        <Camera className={`w-8 h-8 text-yellow-500 ${uploadingRecibos ? 'hidden' : ''}`} />
                        <p className="text-gray-300 font-medium text-sm">
                          {uploadingRecibos ? 'Subiendo...' : 'Tomar Foto'}
                        </p>
                        <p className="text-xs text-gray-500">Usar cámara</p>
                      </div>
                    </label>
                    
                    <label 
                      htmlFor="recibos-gallery" 
                      className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className={`w-8 h-8 text-yellow-500 animate-spin ${uploadingRecibos ? '' : 'hidden'}`} />
                        <FolderOpen className={`w-8 h-8 text-blue-500 ${uploadingRecibos ? 'hidden' : ''}`} />
                        <p className="text-gray-300 font-medium text-sm">
                          {uploadingRecibos ? 'Subiendo...' : 'Elegir Archivos'}
                        </p>
                        <p className="text-xs text-gray-500">Galería/PDF</p>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB c/u • {recibos.length}/10 subidos</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Botón Enviar */}
          <Button
            type="submit"
            disabled={submitting || uploadingCert || uploadingRecibos}
            data-testid="form2-submit-btn"
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold h-14 text-lg"
          >
            {submitting ? 'Enviando...' : 'Enviar Formulario'}
          </Button>
        </form>

        {/* Modal de Confirmación */}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmarEnvio}
          title="Confirmar Envío de Formulario"
          message={`⚠️ INFORMACIÓN IMPORTANTE:\n\nLa información suministrada será revisada por el departamento administrativo y cotejada con nuestros expedientes.\n\nLos recibos físicos que tenga el cliente pierden validez administrativa en nuestro sistema una vez son anexados a la solicitud de este formulario.\n\n¿Estás seguro de continuar?`}
          confirmText="Confirmar y Enviar"
          cancelText="Cancelar"
          isDangerous={false}
        />
      </div>
      </div>
    </div>
  );
}
